import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import colegioApiService from '../services/ColegioApiService';
import { isAdminColegio } from '../utils/adminMenu';
import './NewDashboard.css';
import './AdminDashboard.css';
import './AdminSelectColegio.css';
import './common/DataTable.css';

const emptyForm = {
  nombre: '',
  key: '',
  idOpenAI: '',
  promptOpenAI: '',
  modeloOpenAI: '',
  activo: true
};

const getValue = (source, keys, fallback = '') => {
  if (!source) return fallback;
  const value = keys.map((key) => source[key]).find((item) => item !== undefined && item !== null);
  return value === undefined || value === null ? fallback : value;
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('es-AR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('es-AR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
};

const formatNumber = (value) => Number(value || 0).toLocaleString('es-AR');
const formatUsd = (value) => `USD ${Number(value || 0).toLocaleString('es-AR', {
  minimumFractionDigits: 4,
  maximumFractionDigits: 6
})}`;

const toDateInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDefaultUsageRange = () => {
  const now = new Date();
  return {
    from: toDateInput(new Date(now.getFullYear(), now.getMonth(), 1)),
    to: toDateInput(now),
    assistantConfigId: ''
  };
};

const normalizeConfig = (item) => ({
  id: getValue(item, ['id', 'Id']),
  idColegio: getValue(item, ['idColegio', 'IdColegio']),
  nombre: getValue(item, ['nombre', 'Nombre']),
  key: getValue(item, ['key', 'Key']),
  idOpenAI: getValue(item, ['idOpenAI', 'IdOpenAI']),
  promptOpenAI: getValue(item, ['promptOpenAI', 'PromptOpenAI']),
  modeloOpenAI: getValue(item, ['modeloOpenAI', 'ModeloOpenAI']),
  activo: getValue(item, ['activo', 'Activo'], true),
  fechaAlta: getValue(item, ['fechaAlta', 'FechaAlta'], null),
  fechaModificacion: getValue(item, ['fechaModificacion', 'FechaModificacion'], null),
  keyConfiguradaEnAppSettings: getValue(item, ['keyConfiguradaEnAppSettings', 'KeyConfiguradaEnAppSettings'], false)
});

const normalizeModel = (item) => ({
  model: getValue(item, ['model', 'Model']),
  nombre: getValue(item, ['nombre', 'Nombre']),
  descripcion: getValue(item, ['descripcion', 'Descripcion']),
  caracteristicas: getValue(item, ['caracteristicas', 'Caracteristicas']),
  latencia: getValue(item, ['latencia', 'Latencia']),
  contextWindowTokens: getValue(item, ['contextWindowTokens', 'ContextWindowTokens'], 0),
  maxOutputTokens: getValue(item, ['maxOutputTokens', 'MaxOutputTokens'], 0),
  soportaReasoning: getValue(item, ['soportaReasoning', 'SoportaReasoning'], false),
  reasoningEfforts: getValue(item, ['reasoningEfforts', 'ReasoningEfforts']),
  inputUsdPorMillonTokens: getValue(item, ['inputUsdPorMillonTokens', 'InputUsdPorMillonTokens'], 0),
  cachedInputUsdPorMillonTokens: getValue(item, ['cachedInputUsdPorMillonTokens', 'CachedInputUsdPorMillonTokens'], 0),
  outputUsdPorMillonTokens: getValue(item, ['outputUsdPorMillonTokens', 'OutputUsdPorMillonTokens'], 0),
  notaCostos: getValue(item, ['notaCostos', 'NotaCostos'])
});

const AdminColegioOpenAIAssistantConfigs = () => {
  const { colegioId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isGeneralAdmin = !isAdminColegio(user);

  const [colegio, setColegio] = useState(null);
  const [items, setItems] = useState([]);
  const [configuredKeys, setConfiguredKeys] = useState([]);
  const [modelOptions, setModelOptions] = useState([]);
  const [defaultModel, setDefaultModel] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [usageFilters, setUsageFilters] = useState(getDefaultUsageRange);
  const [usageReport, setUsageReport] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [deleteModal, setDeleteModal] = useState({ open: false, item: null, loading: false });

  const colegioNombre = colegio?.nombre || colegio?.Nombre || 'Colegio';

  const keyOptions = useMemo(() => {
    const keys = new Set(configuredKeys);
    if (form.key) keys.add(form.key);
    return Array.from(keys).sort();
  }, [configuredKeys, form.key]);

  const selectedModel = useMemo(
    () => modelOptions.find((model) => model.model === form.modeloOpenAI) || null,
    [modelOptions, form.modeloOpenAI]
  );

  const monthUsageByAssistant = useMemo(() => {
    const rows = usageReport?.monthToDateAssistantSummaries || [];
    return rows.reduce((acc, row) => {
      const id = getValue(row, ['idAssistantConfig', 'IdAssistantConfig']);
      if (id) acc[id] = row;
      return acc;
    }, {});
  }, [usageReport]);

  const loadData = useCallback(async () => {
    if (!colegioId || !isGeneralAdmin) return;

    setLoading(true);
    setError('');

    try {
      const [colegioResponse, keysResponse, configsResponse] = await Promise.all([
        colegioApiService.getColegioByIdAdmin(colegioId),
        colegioApiService.getOpenAIAssistantKeys(),
        colegioApiService.getOpenAIAssistantConfigs(colegioId, { incluirInactivos: true })
      ]);

      const colegios = colegioResponse?.colegios || colegioResponse?.Colegios || [];
      setColegio(Array.isArray(colegios) ? colegios[0] : null);

      const keys = keysResponse?.keys || keysResponse?.Keys || [];
      const normalizedKeys = Array.isArray(keys) ? keys.filter(Boolean) : [];
      setConfiguredKeys(normalizedKeys);

      const configs = configsResponse?.items || configsResponse?.Items || [];
      setItems(Array.isArray(configs) ? configs.map(normalizeConfig) : []);

      const [modelsResponse, usageResponse] = await Promise.all([
        colegioApiService.getOpenAIAssistantModels(),
        colegioApiService.getOpenAIAssistantUsage(colegioId)
      ]);

      const models = modelsResponse?.models || modelsResponse?.Models || [];
      const normalizedModels = Array.isArray(models) ? models.map(normalizeModel) : [];
      const apiDefaultModel = modelsResponse?.defaultModel || modelsResponse?.DefaultModel || normalizedModels[0]?.model || '';
      setModelOptions(normalizedModels);
      setDefaultModel(apiDefaultModel);
      setUsageReport(usageResponse?.report || usageResponse?.Report || null);
      setForm((prev) => ({
        ...prev,
        modeloOpenAI: prev.modeloOpenAI || apiDefaultModel
      }));

    } catch (err) {
      setError(err.message || 'No pudimos cargar la configuración de asistentes OpenAI.');
    } finally {
      setLoading(false);
    }
  }, [colegioId, isGeneralAdmin]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!editingId && !form.key && configuredKeys.length === 1) {
      setForm((prev) => ({ ...prev, key: configuredKeys[0] }));
    }
  }, [configuredKeys, editingId, form.key]);

  useEffect(() => {
    if (!editingId && !form.modeloOpenAI && defaultModel) {
      setForm((prev) => ({ ...prev, modeloOpenAI: defaultModel }));
    }
  }, [defaultModel, editingId, form.modeloOpenAI]);

  const resetForm = () => {
    setEditingId(null);
    setForm({
      ...emptyForm,
      key: configuredKeys.length === 1 ? configuredKeys[0] : '',
      modeloOpenAI: defaultModel
    });
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setForm({
      nombre: item.nombre || '',
      key: item.key || '',
      idOpenAI: item.idOpenAI || '',
      promptOpenAI: item.promptOpenAI || '',
      modeloOpenAI: item.modeloOpenAI || defaultModel,
      activo: Boolean(item.activo)
    });
    setNotice('');
    setError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setNotice('');

    const payload = {
      nombre: form.nombre.trim(),
      key: form.key.trim(),
      idOpenAI: form.idOpenAI.trim(),
      promptOpenAI: form.promptOpenAI.trim(),
      modeloOpenAI: form.modeloOpenAI || defaultModel,
      activo: Boolean(form.activo)
    };

    try {
      if (editingId) {
        await colegioApiService.updateOpenAIAssistantConfig(colegioId, editingId, payload);
        setNotice('Asistente actualizado y validado con OpenAI correctamente.');
      } else {
        await colegioApiService.createOpenAIAssistantConfig(colegioId, payload);
        setNotice('Asistente creado y validado con OpenAI correctamente.');
      }
      resetForm();
      await loadData();
    } catch (err) {
      setError(err.message || 'No pudimos guardar la configuración.');
    } finally {
      setSaving(false);
    }
  };

  const handleUsageFilterChange = (event) => {
    const { name, value } = event.target;
    setUsageFilters((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const loadUsage = async (event) => {
    if (event) event.preventDefault();
    setUsageLoading(true);
    setError('');

    try {
      const response = await colegioApiService.getOpenAIAssistantUsage(colegioId, usageFilters);
      setUsageReport(response?.report || response?.Report || null);
    } catch (err) {
      setError(err.message || 'No pudimos cargar el consumo de asistentes.');
    } finally {
      setUsageLoading(false);
    }
  };

  const openDeleteModal = (item) => {
    setDeleteModal({ open: true, item, loading: false });
  };

  const closeDeleteModal = () => {
    if (deleteModal.loading) return;
    setDeleteModal({ open: false, item: null, loading: false });
  };

  const handleDelete = async () => {
    if (!deleteModal.item?.id) return;
    setDeleteModal((prev) => ({ ...prev, loading: true }));
    setError('');
    setNotice('');

    try {
      await colegioApiService.deleteOpenAIAssistantConfig(colegioId, deleteModal.item.id);
      setNotice('Asistente eliminado correctamente.');
      setDeleteModal({ open: false, item: null, loading: false });
      if (editingId === deleteModal.item.id) resetForm();
      await loadData();
    } catch (err) {
      setError(err.message || 'No pudimos eliminar el asistente.');
      setDeleteModal((prev) => ({ ...prev, loading: false }));
    }
  };

  if (!isGeneralAdmin) {
    return (
      <div className="admin-select-page">
        <div className="admin-select-card">
          <h1>Sin permisos</h1>
          <p>Esta configuración está disponible solo para el admin general.</p>
          <button className="btn btn-primary" type="button" onClick={() => navigate('/admin-dashboard')}>
            Volver
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="new-dashboard admin-mode">
      <main className="dashboard-main" style={{ marginLeft: 0 }}>
        <header className="dashboard-header admin-dashboard-topbar">
          <div className="header-left">
            <button
              type="button"
              className="sidebar-toggle"
              onClick={() => navigate('/admin/select-colegio')}
              title="Volver"
            >
              <i className="fas fa-arrow-left" />
            </button>
            <div>
              <h1 className="page-title">Asistentes OpenAI fijos</h1>
              <p className="subtitle">Configuración por colegio para consumos internos por API.</p>
            </div>
          </div>
        </header>

        <div className="dashboard-content">
          <section className="profesores-section">
            <div className="section-header">
              <div>
                <p className="eyebrow">Admin general</p>
                <h2>{colegioNombre}</h2>
                <p className="subtitle">
                  Cada registro vincula una key funcional configurada en appsettings con un prompt propio para Responses API.
                </p>
              </div>
              <div className="profesores-actions-header">
                <div className="profesor-count-card">
                  <div className="count-icon">
                    <i className="fas fa-robot" />
                  </div>
                  <div>
                    <p className="count-label">Configs</p>
                    <p className="count-number">{items.length}</p>
                  </div>
                </div>
              </div>
            </div>

            {error && (
              <div className="data-table-error" style={{ marginBottom: '1rem' }}>
                <i className="fas fa-exclamation-triangle" />
                <p>{error}</p>
              </div>
            )}

            {notice && (
              <div className="data-table-empty" style={{ marginBottom: '1rem' }}>
                <div className="empty-icon">
                  <i className="fas fa-check-circle" />
                </div>
                <p>{notice}</p>
              </div>
            )}

            <div className="content-card">
              <div className="card-header">
                <div className="card-title">
                  <i className="fas fa-sliders-h" />
                  <span>{editingId ? 'Editar configuración' : 'Nueva configuración'}</span>
                </div>
              </div>
              <form className="card-content admin-form-grid" onSubmit={handleSubmit}>
                <label className="form-field">
                  <span>Nombre</span>
                  <input
                    type="text"
                    name="nombre"
                    value={form.nombre}
                    onChange={handleChange}
                    placeholder="Ej: Reportes asistentes IA"
                    maxLength={200}
                    required
                  />
                </label>

                <label className="form-field">
                  <span>Key appsettings</span>
                  {keyOptions.length > 0 ? (
                    <select name="key" value={form.key} onChange={handleChange} required>
                      <option value="">Seleccionar key</option>
                      {keyOptions.map((key) => (
                        <option key={key} value={key}>{key}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      name="key"
                      value={form.key}
                      onChange={handleChange}
                      placeholder="reportes_asistentes_clasicos_gpt"
                      maxLength={120}
                      required
                    />
                  )}
                </label>

                <label className="form-field prompt-textarea-field">
                  <span>Prompt del asistente</span>
                  <textarea
                    name="promptOpenAI"
                    value={form.promptOpenAI}
                    onChange={handleChange}
                    placeholder="Sos un asistente especializado en reportes escolares. Respondé de forma clara, breve y accionable..."
                    maxLength={20000}
                    rows={9}
                    required
                  />
                </label>

                <label className="form-field">
                  <span>Modelo</span>
                  <select name="modeloOpenAI" value={form.modeloOpenAI} onChange={handleChange} required>
                    <option value="">Seleccionar modelo</option>
                    {modelOptions.map((model) => (
                      <option key={model.model} value={model.model}>
                        {model.nombre || model.model}
                      </option>
                    ))}
                  </select>
                </label>

                {selectedModel && (
                  <div className="model-info-panel">
                    <div>
                      <strong>{selectedModel.nombre || selectedModel.model}</strong>
                      <span>{selectedModel.descripcion}</span>
                    </div>
                    <div className="model-info-grid">
                      <span>Entrada: {formatUsd(selectedModel.inputUsdPorMillonTokens)} / 1M tokens</span>
                      <span>Cache: {formatUsd(selectedModel.cachedInputUsdPorMillonTokens)} / 1M tokens</span>
                      <span>Salida: {formatUsd(selectedModel.outputUsdPorMillonTokens)} / 1M tokens</span>
                      <span>Contexto: {formatNumber(selectedModel.contextWindowTokens)} tokens</span>
                      <span>Salida máx.: {formatNumber(selectedModel.maxOutputTokens)} tokens</span>
                      <span>Latencia: {selectedModel.latencia || '—'}</span>
                    </div>
                    <p>{selectedModel.caracteristicas}</p>
                    <small>{selectedModel.notaCostos}</small>
                  </div>
                )}

                <label className="form-field checkbox-field">
                  <input
                    type="checkbox"
                    name="activo"
                    checked={form.activo}
                    onChange={handleChange}
                  />
                  <span>Activo</span>
                </label>

                <div className="form-actions">
                  {editingId && (
                    <button className="btn-secondary" type="button" onClick={resetForm} disabled={saving}>
                      Cancelar edición
                    </button>
                  )}
                  <button className="primary-btn" type="submit" disabled={saving || loading}>
                    <i className={`fas ${saving ? 'fa-spinner fa-spin' : 'fa-save'}`} />
                    <span>{saving ? 'Guardando...' : 'Guardar'}</span>
                  </button>
                </div>
              </form>
            </div>

            <div className="content-card">
              <div className="card-header">
                <div className="card-title">
                  <i className="fas fa-list" />
                  <span>Configuraciones cargadas</span>
                </div>
                <div className="professor-actions">
                  <button className="refresh-btn" type="button" onClick={loadData} disabled={loading}>
                    <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} />
                  </button>
                </div>
              </div>

              <div className="card-content professors-table">
                {loading && (
                  <div className="data-table-loading">
                    <div className="loading-spinner">
                      <i className="fas fa-spinner fa-spin" />
                    </div>
                    <p>Cargando configuraciones...</p>
                  </div>
                )}

                {!loading && items.length === 0 && (
                  <div className="data-table-empty">
                    <i className="fas fa-box-open" />
                    <p>No hay asistentes OpenAI fijos configurados para este colegio.</p>
                  </div>
                )}

                {!loading && items.length > 0 && (
                  <div className="data-table-wrapper">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Nombre</th>
                          <th>Key</th>
                          <th>Prompt</th>
                          <th>Modelo</th>
                          <th>Tokens mes</th>
                          <th>Costo mes</th>
                          <th>Alta</th>
                          <th>Modificación</th>
                          <th className="text-center width-sm">Estado</th>
                          <th className="text-center width-sm">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item) => (
                          <tr key={item.id}>
                            <td>
                              <div className="prof-name">
                                <span className="name">{item.nombre}</span>
                                <span className="muted">{item.id}</span>
                              </div>
                            </td>
                            <td>
                              <span className={`status-chip ${item.keyConfiguradaEnAppSettings ? 'success' : 'warning'}`}>
                                {item.key}
                              </span>
                            </td>
                            <td>
                              <div className="prompt-preview">
                                {item.promptOpenAI || item.idOpenAI || '—'}
                              </div>
                            </td>
                            <td>
                              <span className="status-chip info">{item.modeloOpenAI || '—'}</span>
                            </td>
                            <td>{formatNumber(getValue(monthUsageByAssistant[item.id], ['totalTokens', 'TotalTokens'], 0))}</td>
                            <td>{formatUsd(getValue(monthUsageByAssistant[item.id], ['totalCostUsd', 'TotalCostUsd'], 0))}</td>
                            <td>{formatDateTime(item.fechaAlta)}</td>
                            <td>{formatDateTime(item.fechaModificacion)}</td>
                            <td className="text-center">
                              <span className={`status-chip ${item.activo ? 'success' : 'danger'}`}>
                                {item.activo ? 'Activo' : 'Inactivo'}
                              </span>
                            </td>
                            <td className="text-center">
                              <div className="table-actions">
                                <button
                                  className="table-btn table-btn-edit"
                                  type="button"
                                  title="Editar"
                                  onClick={() => handleEdit(item)}
                                >
                                  <i className="fas fa-edit" />
                                </button>
                                <button
                                  className="table-btn table-btn-delete-text"
                                  type="button"
                                  title="Eliminar"
                                  onClick={() => openDeleteModal(item)}
                                >
                                  <i className="fas fa-trash-alt" />
                                  <span>Eliminar</span>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            <div className="content-card">
              <div className="card-header">
                <div className="card-title">
                  <i className="fas fa-chart-line" />
                  <span>Consumo de asistentes</span>
                </div>
              </div>
              <div className="card-content">
                <form className="usage-filter-bar" onSubmit={loadUsage}>
                  <label className="form-field">
                    <span>Asistente</span>
                    <select
                      name="assistantConfigId"
                      value={usageFilters.assistantConfigId}
                      onChange={handleUsageFilterChange}
                    >
                      <option value="">Todos</option>
                      {items.map((item) => (
                        <option key={item.id} value={item.id}>{item.nombre}</option>
                      ))}
                    </select>
                  </label>
                  <label className="form-field">
                    <span>Desde</span>
                    <input type="date" name="from" value={usageFilters.from} onChange={handleUsageFilterChange} />
                  </label>
                  <label className="form-field">
                    <span>Hasta</span>
                    <input type="date" name="to" value={usageFilters.to} onChange={handleUsageFilterChange} />
                  </label>
                  <button className="primary-btn" type="submit" disabled={usageLoading}>
                    <i className={`fas ${usageLoading ? 'fa-spinner fa-spin' : 'fa-search'}`} />
                    <span>{usageLoading ? 'Consultando...' : 'Consultar'}</span>
                  </button>
                </form>

                {usageReport && (
                  <>
                    <div className="usage-summary-grid">
                      <div className="usage-summary-card">
                        <span>Rango seleccionado</span>
                        <strong>{formatUsd(getValue(usageReport.summary, ['totalCostUsd', 'TotalCostUsd'], 0))}</strong>
                        <small>
                          {formatNumber(getValue(usageReport.summary, ['totalTokens', 'TotalTokens'], 0))} tokens · {' '}
                          {formatNumber(getValue(usageReport.summary, ['requestsCount', 'RequestsCount'], 0))} consultas
                        </small>
                      </div>
                      <div className="usage-summary-card">
                        <span>Mes actual</span>
                        <strong>{formatUsd(getValue(usageReport.monthToDate, ['totalCostUsd', 'TotalCostUsd'], 0))}</strong>
                        <small>
                          {formatNumber(getValue(usageReport.monthToDate, ['totalTokens', 'TotalTokens'], 0))} tokens · {' '}
                          {formatNumber(getValue(usageReport.monthToDate, ['requestsCount', 'RequestsCount'], 0))} consultas
                        </small>
                      </div>
                      <div className="usage-summary-card">
                        <span>Entrada / salida</span>
                        <strong>
                          {formatNumber(getValue(usageReport.summary, ['inputTokens', 'InputTokens'], 0))} / {formatNumber(getValue(usageReport.summary, ['outputTokens', 'OutputTokens'], 0))}
                        </strong>
                        <small>tokens de entrada y salida en el rango</small>
                      </div>
                    </div>

                    <div className="usage-section-title">Resumen por asistente</div>
                    {(usageReport.assistantSummaries || []).length === 0 ? (
                      <div className="data-table-empty compact-empty">
                        <p>No hay consumos registrados para el rango seleccionado.</p>
                      </div>
                    ) : (
                      <div className="data-table-wrapper">
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>Asistente</th>
                              <th>Modelo</th>
                              <th>Consultas</th>
                              <th>Entrada</th>
                              <th>Cache</th>
                              <th>Salida</th>
                              <th>Total tokens</th>
                              <th>Costo aprox.</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(usageReport.assistantSummaries || []).map((row) => (
                              <tr key={`${getValue(row, ['idAssistantConfig', 'IdAssistantConfig'])}-${getValue(row, ['modeloOpenAI', 'ModeloOpenAI'])}`}>
                                <td>{getValue(row, ['assistantNombre', 'AssistantNombre'])}</td>
                                <td>{getValue(row, ['modeloOpenAI', 'ModeloOpenAI'])}</td>
                                <td>{formatNumber(getValue(row, ['requestsCount', 'RequestsCount'], 0))}</td>
                                <td>{formatNumber(getValue(row, ['inputTokens', 'InputTokens'], 0))}</td>
                                <td>{formatNumber(getValue(row, ['cachedInputTokens', 'CachedInputTokens'], 0))}</td>
                                <td>{formatNumber(getValue(row, ['outputTokens', 'OutputTokens'], 0))}</td>
                                <td>{formatNumber(getValue(row, ['totalTokens', 'TotalTokens'], 0))}</td>
                                <td>{formatUsd(getValue(row, ['totalCostUsd', 'TotalCostUsd'], 0))}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    <div className="usage-section-title">Detalle diario</div>
                    {(usageReport.items || []).length === 0 ? (
                      <div className="data-table-empty compact-empty">
                        <p>No hay detalle diario para mostrar.</p>
                      </div>
                    ) : (
                      <div className="data-table-wrapper">
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>Día</th>
                              <th>Asistente</th>
                              <th>Modelo</th>
                              <th>Consultas</th>
                              <th>Tokens</th>
                              <th>Costo aprox.</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(usageReport.items || []).map((row) => (
                              <tr key={`${getValue(row, ['dia', 'Dia'])}-${getValue(row, ['idAssistantConfig', 'IdAssistantConfig'])}-${getValue(row, ['modeloOpenAI', 'ModeloOpenAI'])}`}>
                                <td>{formatDate(getValue(row, ['dia', 'Dia']))}</td>
                                <td>{getValue(row, ['assistantNombre', 'AssistantNombre'])}</td>
                                <td>{getValue(row, ['modeloOpenAI', 'ModeloOpenAI'])}</td>
                                <td>{formatNumber(getValue(row, ['requestsCount', 'RequestsCount'], 0))}</td>
                                <td>{formatNumber(getValue(row, ['totalTokens', 'TotalTokens'], 0))}</td>
                                <td>{formatUsd(getValue(row, ['totalCostUsd', 'TotalCostUsd'], 0))}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </section>
        </div>
      </main>

      {deleteModal.open && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon">
              <i className="fas fa-trash-alt" />
            </div>
            <h3>Eliminar asistente</h3>
            <p>
              Se eliminará definitivamente el asistente "{deleteModal.item?.nombre}" y todos sus consumos relacionados.
            </p>
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={closeDeleteModal} disabled={deleteModal.loading}>
                Cancelar
              </button>
              <button type="button" className="btn-primary danger" onClick={handleDelete} disabled={deleteModal.loading}>
                <i className={`fas ${deleteModal.loading ? 'fa-spinner fa-spin' : 'fa-trash-alt'}`} />
                <span>{deleteModal.loading ? 'Eliminando...' : 'Sí, eliminar'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminColegioOpenAIAssistantConfigs;
